import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import Collection from 'assets/components/data/Collection';
import ThemeCollectionTable from 'assets/components/data/themes/Theme.Collection.Table';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import useForm from 'assets/components/form/hooks/Form';
import FormatValue from 'assets/components/formatValue/FormatValue';
import Icon from 'assets/components/icon/Icon';
import CheckInput from 'assets/components/inputs/check/CheckInput';
import FileInput from 'assets/components/inputs/file/FileInput';
import ThemeFileInputDropzone from 'assets/components/inputs/file/themes/Theme.FileInput.Dropzone';
import { inputStyles } from 'assets/components/inputs/Input';
import MultilineTextInput from 'assets/components/inputs/multilineText/MultilineTextInput';
import TextInput from 'assets/components/inputs/text/TextInput';
import ErrorWrapper from 'assets/components/inputs/wrappers/ErrorWrapper';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import ThemeWrapperPaper from 'assets/themes/wrappers/Theme.Wrapper.Paper';
import { combineStrings } from 'assets/utils/data/String';
import { HttpStatus } from 'config/Api.Config';
import Product from 'models/productManagement/product/Model.Product';
import contractDocumentDeleteApi from 'models/sales/contract/contractDocument/delete/Api.ContractDocument.Delete';
import contractDocumentSaveApi from 'models/sales/contract/contractDocument/save/Api.ContractDocument.Save';
import contractDocumentationAggregateSaveApi from 'models/sales/contract/contractDocumentationAggregate/save/Api.ContractDocumentationAggregate.Save';
import { useEffect, useMemo, useState } from 'react';
import { useSalesContractProvider } from '../../Sales.Contract.Hooks';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import { isEmpty } from 'lodash';
import contractDocumentAggregateTranslationSaveApi from 'models/sales/contract/contractDocumentAggregateTranslation/save/Api.ContractDocumentAggregateTranslation.Save';
import contractDocumentAggregateTranslationDeleteApi from 'models/sales/contract/contractDocumentAggregateTranslation/delete/Api.ContractDocumentAggregateTranslation.Delete';

export default function ContractInfoDocuments() {
  const { lang, translate } = getLocales();
  const { selected, subfeeDocs, findApi, permissions, contractRefund } = useSalesContractProvider();
  const productApi = useApi({
    action: Product.find,
    wait: true,
    body: {
      id: selected?.productId,
    },
  });
  useEffect(() => {
    if (selected?.productId) productApi.execute((b) => b);
    else productApi.reset(true);
  }, [selected?.productId]);

  const { execute: deleteDoc } = useApi({
    action: contractDocumentDeleteApi,
    wait: true,
    callback: () => findApi.execute((b) => b),
  });
  const { execute: saveDoc, isExecuting: isExecutingSavingContractDoc } = useApi({
    action: contractDocumentSaveApi,
    wait: true,
    callback: () => findApi.execute((b) => b),
  });
  const onDelete = (documentId: number) => deleteDoc({ id: selected?.id, documentId });

  const docForm = useForm<Utils.FormData<Model.IContractDocument>>({
    default: {
      contractId: selected?.id,
    },
    validation: (data, errors) => {
      if (!data.formFile) errors.formFile = lang.required;
    },
    onSubmit: saveDoc,
  });
  useEffect(() => {
    if (docForm?.data?.contractId !== selected?.id) {
      docForm.update({ contractId: selected?.id });
    }
  }, [selected?.id]);
  const formFile = useMemo(
    () =>
      docForm.data.formFile && {
        original: docForm.data.formFile,
        type: docForm.data.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(docForm.data.formFile),
      },
    [docForm.data.formFile]
  );

  //#region Contract Documentation Aggregate
  const [showUploadDoc, setShowUploadDoc] = useState<boolean>(false);
  const { execute: saveDocAggregate, isExecuting: isSaveDocAggregateExecuting } = useApi({
    action: contractDocumentationAggregateSaveApi,
    wait: true,
    callback: (_, res) => {
      if (res.status !== HttpStatus.ok) return addLog({ error: res.message });

      findApi.execute((b) => b);
      setShowUploadDoc(false);
      docAggForm.reload();
      addLog({ success: lang.saveSuccess });
    },
  });
  const docAggForm = useForm<Utils.FormData<Api.Core.ContractDocumentationAggregate.Save.IRequest>>({
    default: {
      contractId: selected?.id,
    },
    validation: (data, errors) => {
      if (!data.formFile) errors.formFile = lang.required;
    },
    onSubmit: saveDocAggregate,
  });
  const docAggFormFile = useMemo(
    () =>
      docAggForm.data.formFile && {
        original: docAggForm.data.formFile,
        type: docAggForm.data.formFile.type as Utils.File.MimeType,
        dataUrl: window.URL.createObjectURL(docAggForm.data.formFile),
      },
    [docAggForm.data.formFile]
  );

  const { execute: generateDocAggTranslation, isExecuting: isGeneratingDocTranslationAgg } = useApi({
    action: contractDocumentAggregateTranslationSaveApi,
    wait: true,
    callback: (_, res) => {
      if (res.status !== HttpStatus.ok) return addLog({ error: res.message });
      findApi.execute((b) => b);
      generateDocAggTranslationForm.reload();
      addLog({ success: lang.saveSuccess });
    },
  });
  const generateDocAggTranslationForm = useForm<
    Utils.FormData<Api.Core.ContractDocumentAggregateTranslation.Save.IRequest>
  >({
    default: {
      id: selected?.id,
      languageIsoCode: undefined,
    },
    validation: (data, errors) => {
      if (!data.languageIsoCode) errors.languageIsoCode = lang.required;
    },
    onSubmit: (data) => {
      const exists = selected?.contractDocumentAggregateTranslations?.some(
        (it) => it.contractId === data?.id && it.languageIsoCode === data.languageIsoCode
      );
      if (exists) {
        addLog({ warning: lang.documentExistsForThisLang });
        return;
      }
      generateDocAggTranslation(data);
    },
  });
  const { execute: deleteDocAggTranslation } = useApi({
    action: contractDocumentAggregateTranslationDeleteApi,
    wait: true,
    callback: () => findApi.execute((b) => b),
  });

  const onDeleteDocAggTranslation = (docId: number) => deleteDocAggTranslation({ id: docId });
  //#endregion

  return (
    <>
      <Collection class={combineStrings(' ', ThemeCollectionTable, ThemeWrapperPaper)} style={{ padding: 0 }}>
        <div
          style={{
            fontSize: Theme.FontSize.L,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {lang.documentationAggregate}
          <div
            style={{
              fontSize: Theme.FontSize.M,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Button
              class={ThemeButtonCircle}
              onClick={setShowUploadDoc}
              data={showUploadDoc ? false : true}
              media={showUploadDoc ? <Icon class="fas-ban" /> : <Icon class="fas-edit" />}
              htmlElementProps={{ title: showUploadDoc ? lang.cancel : lang.edit }}
            />
            <Button
              render={lang.add}
              media={<Icon class="fas-upload" />}
              onClick={docAggForm.submit}
              disabled={docAggForm.hasErrors}
              active={!docAggForm.hasErrors}
              class={ThemeButtonCircle}
              isLoading={isSaveDocAggregateExecuting}
              htmlElementProps={{ title: lang.add }}
            />
          </div>
        </div>
        {showUploadDoc && (
          <FileInput
            name="file"
            fileLimit={1}
            value={docAggFormFile ? [docAggFormFile] : []}
            onChange={({ file }) => {
              const formFile = file && file[0]?.original;
              docAggForm.update({ formFile });
            }}
            renderBefore={
              <CheckInput
                label={lang.signManually}
                value={docAggForm.data?.isSignedManually}
                onChange={docAggForm.update}
                name="isSignedManually"
                class={inputStyles.check.checkbox}
              />
            }
            style={{ width: '75%', margin: '10px auto' }}
            class={ThemeFileInputDropzone}
            error={docAggForm.errors.formFile?._objectError}
          />
        )}
        {contractRefund?.documentationAggregate && (
          <Card
            class={ThemeCardRow}
            info={lang.refundDocument}
            style={{ borderTop: 'var(--sizeXXS) solid var(--colorShadow)' }}
            title={contractRefund?.documentationAggregate?.name}
            media={<Icon class="fas-folder" />}
            actions={
              <>
                <LinkButton
                  class={ThemeButtonCircle}
                  url={contractRefund?.documentationAggregate?.url}
                  media={<Icon class="fas-download" />}
                  target="_blank"
                  htmlElementProps={{ title: lang.download }}
                />
              </>
            }
          />
        )}
        {selected?.documentationAggregate || !!selected.transferDocuments?.length ? (
          <>
            <Card
              class={ThemeCardRow}
              info={lang.contractDocument}
              style={showUploadDoc && { borderTop: 'var(--sizeXXS) solid var(--colorShadow)' }}
              title={selected?.documentationAggregate?.name}
              media={<Icon class="fas-folder" />}
              actions={
                <>
                  <LinkButton
                    class={ThemeButtonCircle}
                    url={selected?.documentationAggregate?.url}
                    media={<Icon class="fas-download" />}
                    target="_blank"
                    htmlElementProps={{ title: lang.download }}
                  />
                </>
              }
            />
            <DataMap
              data={selected.transferDocuments ?? []}
              render={({ data: transferDoc }) => (
                <Card
                  class={ThemeCardRow}
                  info={lang.transferDocument}
                  style={showUploadDoc && { borderTop: 'var(--sizeXXS) solid var(--colorShadow)' }}
                  title={transferDoc?.name}
                  media={<Icon class="fas-folder" />}
                  actions={
                    <>
                      <LinkButton
                        class={ThemeButtonCircle}
                        url={transferDoc?.url}
                        media={<Icon class="fas-download" />}
                        target="_blank"
                        htmlElementProps={{ title: lang.download }}
                      />
                    </>
                  }
                />
              )}
            />
          </>
        ) : (
          <ErrorWrapper children={lang.noFiles} />
        )}
      </Collection>
      &nbsp;
      <Collection class={combineStrings(' ', ThemeCollectionTable, ThemeWrapperPaper)} style={{ padding: 0 }}>
        <div
          style={{
            fontSize: Theme.FontSize.L,
            fontWeight: 'bold',
            display: 'flex',
            flexDirection: 'column',
            gap: Theme.Size.M,
          }}
        >
          {lang.documentationTranslationAggregate}
          <div
            style={{
              fontSize: Theme.FontSize.M,
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <SelectInput
              name="languageIsoCode"
              // label={lang.language}
              data={Object.values(Language).map((val) => ({ id: val, title: translate(val) }))}
              value={generateDocAggTranslationForm.data?.languageIsoCode}
              onChange={generateDocAggTranslationForm.update}
              icon={<Icon class="fas-language" />}
              error={generateDocAggTranslationForm?.errors?.languageIsoCode}
              style={{
                width: '25%',
              }}
            />
            <Button
              render={lang.generate}
              media={<Icon class="fas-upload" />}
              onClick={generateDocAggTranslationForm.submit}
              disabled={generateDocAggTranslationForm.hasErrors}
              active={!generateDocAggTranslationForm.hasErrors}
              class={ThemeButtonCircle}
              isLoading={isGeneratingDocTranslationAgg}
              htmlElementProps={{ title: lang.generate }}
            />
          </div>
        </div>
        {!isEmpty(selected?.contractDocumentAggregateTranslations) ? (
          <DataMap
            data={selected?.contractDocumentAggregateTranslations}
            render={({ data: doc, index }) => (
              <Card
                key={index}
                class={ThemeCardRow}
                info={lang.generated}
                title={doc.mediaFile?.name}
                subtitle={lang[doc.languageIsoCode]}
                media={<Icon class="fas-folder" />}
                actions={
                  <>
                    <LinkButton
                      class={ThemeButtonCircle}
                      url={doc.mediaFile?.url}
                      media={<Icon class="fas-download" />}
                      target="_blank"
                      htmlElementProps={{ title: lang.download }}
                    />
                    <Button
                      class={ThemeButtonCircle}
                      onClick={onDeleteDocAggTranslation}
                      data={doc.id}
                      media={<Icon class="fas-trash" />}
                      disabled={!doc.id}
                      htmlElementProps={{ title: lang.delete }}
                    />
                  </>
                }
              />
            )}
          />
        ) : (
          <ErrorWrapper children={lang.noFiles} />
        )}
      </Collection>
      &nbsp;
      <Collection class={combineStrings(' ', ThemeCollectionTable, ThemeWrapperPaper)} style={{ padding: 0 }}>
        <div
          style={{
            fontSize: Theme.FontSize.L,
            fontWeight: 'bold',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {lang.documents}
        </div>
        {!selected.productDocuments?.length &&
          !subfeeDocs?.length &&
          !selected.documents?.length &&
          !selected.contractDocuments?.length && <ErrorWrapper children={lang.noFiles} />}
        <DataMap
          data={selected.documents ?? []}
          render={({ data: doc, index }) => (
            <Card
              key={index}
              class={ThemeCardRow}
              title={doc.name}
              info={lang.generated}
              media={<Icon class="fas-file" />}
              actions={
                <LinkButton
                  class={ThemeButtonCircle}
                  url={doc.url}
                  media={<Icon class="fas-download" />}
                  target="_blank"
                  disabled={!doc.id}
                  htmlElementProps={{ title: lang.download }}
                />
              }
            />
          )}
        />
        <DataMap
          data={subfeeDocs}
          render={({ data: doc, index }) => (
            <Card
              key={index}
              class={ThemeCardRow}
              title={doc.mediaFile.name}
              info={lang.surcharge}
              media={<Icon class="fas-dollar-sign" />}
              actions={
                <>
                  <LinkButton
                    class={ThemeButtonCircle}
                    url={doc.mediaFile.url}
                    media={<Icon class="fas-download" />}
                    target="_blank"
                    disabled={!doc.id}
                    htmlElementProps={{ title: lang.download }}
                  />
                </>
              }
            />
          )}
        />
        <DataMap
          data={selected.productDocuments}
          render={({ data: doc, index }) => (
            <Card
              key={index}
              class={ThemeCardRow}
              title={doc.name}
              info={lang.uploaded}
              subtitle={combineStrings(': ', lang.notes, doc.notes)}
              media={<Icon class="fas-upload" />}
              actions={
                <LinkButton
                  class={ThemeButtonCircle}
                  url={doc.mediaFile.url}
                  media={<Icon class="fas-download" />}
                  target="_blank"
                  disabled={!doc.id}
                  htmlElementProps={{ title: lang.download }}
                />
              }
            />
          )}
        />
        <DataMap
          data={selected.contractDocuments}
          render={({ data: doc, index }) => (
            <Card
              key={index}
              class={ThemeCardRow}
              title={doc.name}
              info={
                <>
                  {lang.uploadedBy}{' '}
                  {combineStrings(' ', doc.createdBy?.firstName, doc.createdBy?.lastName) ?? lang.unknownUser} {lang.at}{' '}
                  <FormatValue value={doc.createdAtUtc} />
                </>
              }
              subtitle={combineStrings(': ', lang.notes, doc.notes)}
              media={<Icon class="fas-file-upload" />}
              actions={
                <>
                  <LinkButton
                    class={ThemeButtonCircle}
                    url={doc.mediaFile.url}
                    media={<Icon class="fas-download" />}
                    target="_blank"
                    disabled={!doc.id}
                    htmlElementProps={{ title: lang.download }}
                  />
                  <Button
                    class={ThemeButtonCircle}
                    onClick={onDelete}
                    data={doc.id}
                    media={<Icon class="fas-trash" />}
                    disabled={!doc.id || !permissions.UPLOAD_DOCUMENTS_CONTRACT}
                    htmlElementProps={{ title: lang.delete }}
                  />
                </>
              }
            />
          )}
        />
      </Collection>
      {permissions.UPLOAD_DOCUMENTS_CONTRACT && (
        <>
          <br />
          <FormWrapper class={ThemeWrapperPaper}>
            <h3>{lang.additionalDocumentation}</h3>
            <TextInput
              name="name"
              label={lang.name}
              value={docForm.data.name}
              onChange={docForm.update}
              error={docForm.errors.name}
            />
            <MultilineTextInput
              name="notes"
              label={lang.notes}
              value={docForm.data.notes}
              onChange={docForm.update}
              error={docForm.errors.notes}
            />
            <FileInput
              name="file"
              fileLimit={1}
              value={formFile ? [formFile] : []}
              onChange={({ file }) => {
                const formFile = file && file[0]?.original;
                docForm.update({ formFile });
              }}
              class={ThemeFileInputDropzone}
              error={docForm.errors.formFile?._objectError}
            />
            <Button
              render={lang.add}
              media={<Icon class="fas-upload" />}
              onClick={docForm.submit}
              disabled={docForm.hasErrors}
              isLoading={docForm.isSubmitted || isExecutingSavingContractDoc}
              active={!docForm.hasErrors}
              class={ThemeButtonCircle}
              htmlElementProps={{ title: lang.add }}
            />
          </FormWrapper>
        </>
      )}
    </>
  );
}
